"use client";

import { Heart, Tag, Zap } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { useCartStore } from "@/components/providers/cart-store";
import { useWishlistStore } from "@/components/providers/wishlist-store";
import { formatPrice } from "@/lib/utils";
import { cn } from "@/lib/utils";
import type { Product } from "@/types";

type ProductCardProps = {
  product: Product;
  compact?: boolean;
  sponsored?: boolean;
};

export function ProductCard({ product, compact = false, sponsored = false }: ProductCardProps) {
  const addItem = useCartStore((s) => s.addItem);
  const toggleWishlist = useWishlistStore((s) => s.toggle);
  const isWishlisted = useWishlistStore((s) => s.has(product.id));
  const [mounted, setMounted] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => setMounted(true), []);

  const safeIsWishlisted = mounted ? isWishlisted : false;
  const savings = product.mrp - product.price;
  const isDeal = product.isFlashDeal || product.discount >= 20;

  function addToCart() {
    setAdding(true);
    addItem(product, 1);
    toast.success("Added to cart", { description: product.title });
    setTimeout(() => setAdding(false), 300);
  }

  // Sold count representation matching the screenshot (e.g. 1,000+ sold, 147 sold)
  const soldCount =
    product.reviewCount >= 1000
      ? "1,000+ sold"
      : product.reviewCount > 0
      ? `${product.reviewCount} sold`
      : "100+ sold";

  return (
    <article className="group relative flex h-full flex-col overflow-hidden rounded-[10px] bg-white transition-all duration-200 hover:shadow-md dark:bg-slate-900 border border-slate-100 dark:border-white/5">
      {/* Product Image Area (Edge-to-edge square) */}
      <Link
        href={`/products/${product.slug}`}
        className="relative block aspect-square w-full overflow-hidden bg-[#f7f7f7] dark:bg-slate-800"
      >
        <Image
          src={product.images[0] || "/placeholder.png"}
          alt={product.title}
          fill
          sizes="(max-width: 640px) 50vw, (max-width: 1024px) 25vw, 16vw"
          className="object-contain p-1.5 transition duration-300 group-hover:scale-[1.03]"
        />

        {/* Brand Tag (e.g. Lenovo on top-left) */}
        {product.brand && product.brand.toLowerCase() === "lenovo" && (
          <div className="absolute left-1.5 top-1.5 z-10 rounded-xs bg-[#e22] px-1.5 py-0.5 text-[9px] font-black uppercase tracking-tight text-white shadow-xs">
            Lenovo
          </div>
        )}

        {/* Wishlist Button (touch-accessible on mobile, hover on desktop) */}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleWishlist(product);
            toast.success(isWishlisted ? "Removed from wishlist" : "Saved to wishlist");
          }}
          className={cn(
            "absolute right-1.5 top-1.5 z-10 flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-full bg-white/90 p-1 text-slate-500 shadow-xs transition sm:opacity-0 sm:group-hover:opacity-100 hover:text-red-500 dark:bg-slate-800/90 dark:text-slate-300",
            safeIsWishlisted && "!opacity-100 text-red-500 fill-red-500"
          )}
          aria-label="Wishlist"
        >
          <Heart className={cn("h-3.5 w-3.5", safeIsWishlisted && "fill-current")} />
        </button>

        {/* Welcome Deal / Free Shipping Ribbon Banner at bottom-left */}
        {(product.isFlashDeal || product.discount >= 30) && (
          <div className="absolute bottom-0 left-0 z-10 max-w-[85%] truncate rounded-tr-md bg-gradient-to-r from-[#ff0055] to-[#ff1a75] px-1.5 sm:px-2 py-0.5 text-[8.5px] sm:text-[10px] font-black italic uppercase tracking-tight text-white shadow-xs">
            WELCOME DEAL • Free shipping
          </div>
        )}

        {/* Floating Circular Quick Add-To-Cart Button at bottom-right */}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            addToCart();
          }}
          disabled={adding || product.stock <= 0}
          className="absolute bottom-2 right-2 z-10 flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-full bg-white text-slate-900 shadow-md border border-slate-200/90 transition hover:scale-110 active:scale-95 hover:bg-slate-900 hover:text-white dark:bg-slate-800 dark:text-white dark:border-white/10 dark:hover:bg-amazon-orange disabled:opacity-50"
          aria-label={`Add ${product.title} to cart`}
          title="Add to cart"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-3.5 w-3.5 sm:h-4 sm:w-4"
          >
            <circle cx="8" cy="21" r="1" />
            <circle cx="19" cy="21" r="1" />
            <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
            <path d="M16 8h4" />
            <path d="M18 6v4" />
          </svg>
        </button>
      </Link>

      {/* Content Area Below Image */}
      <div className="flex flex-1 flex-col justify-between p-1.5 sm:p-2.5">
        <div>
          {/* Title: 1 line with ellipsis */}
          <Link
            href={`/products/${product.slug}`}
            className="block truncate text-[11px] sm:text-[13px] font-normal leading-tight text-slate-800 hover:text-[#ff0055] dark:text-slate-200 dark:hover:text-amazon-orange"
            title={product.title}
          >
            {product.title}
          </Link>

          {/* Price Row: [Bold current price] [line-through MRP] [-discount%] */}
          <div className="mt-1 flex flex-wrap items-baseline gap-1 sm:gap-1.5 leading-none">
            <span
              className={cn(
                "text-xs sm:text-sm md:text-base font-extrabold tracking-tight",
                isDeal ? "text-[#f32]" : "text-slate-950 dark:text-white"
              )}
            >
              {formatPrice(product.price)}
            </span>
            {savings > 0 && (
              <span className="text-[10px] sm:text-[11px] text-slate-400 line-through">
                {formatPrice(product.mrp)}
              </span>
            )}
            {product.discount > 0 && (
              <span className="text-[10px] sm:text-[11px] font-bold text-[#f32]">
                -{product.discount}%
              </span>
            )}
          </div>

          {/* Rating Stars & Sold Count */}
          <div className="mt-1 flex items-center gap-1 leading-none">
            {/* 5 mini solid black stars */}
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((star) => (
                <svg
                  key={star}
                  viewBox="0 0 24 24"
                  className={cn(
                    "h-2 w-2 sm:h-2.5 sm:w-2.5",
                    star <= Math.round(product.rating || 4.5)
                      ? "fill-slate-900 text-slate-900 dark:fill-white dark:text-white"
                      : "fill-slate-200 text-slate-200 dark:fill-slate-700 dark:text-slate-700"
                  )}
                >
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
              ))}
            </div>
            <span className="text-[10px] sm:text-[11px] font-medium text-slate-700 dark:text-slate-300">
              {product.rating > 0 ? product.rating.toFixed(1) : "4.5"}
            </span>
            <span className="text-slate-300 dark:text-slate-600">|</span>
            <span className="text-[10px] sm:text-[11px] text-slate-500 dark:text-slate-400 truncate">
              {soldCount}
            </span>
          </div>
        </div>

        {/* Incentive Tag Line */}
        {product.isFlashDeal ? (
          <div className="mt-1 flex items-center gap-1 text-[10px] sm:text-[11px] font-medium text-[#f32] leading-none">
            <Zap className="h-2.5 w-2.5 sm:h-3 sm:w-3 fill-[#f32] text-[#f32] shrink-0" />
            <span className="truncate">Returning shoppers save ETB 50</span>
          </div>
        ) : product.discount >= 19 ? (
          <div className="mt-1 flex items-center gap-1 text-[10px] sm:text-[11px] font-medium text-[#f32] leading-none">
            <Tag className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-[#f32] shrink-0" />
            <span className="truncate">Best price in similar deals</span>
          </div>
        ) : (
          <div className="mt-1 h-3 sm:h-3.5" />
        )}
      </div>
    </article>
  );
}
